'use client';

import { useState } from 'react';
import { AppDialog } from './AppDialog';

/**
 * 2026-06-05 — persistent banner when an admin is impersonating a user.
 *
 * Renders above the TopBar so it shows on every authenticated page. The
 * exit button POSTs to /api/platform/impersonate/exit which clears the
 * agentx_impersonate cookie, then hard-navigates to /platform so the
 * admin lands back where they came from with full admin context.
 */
export function ImpersonationBanner({
  adminEmail,
  adminDisplayName,
  targetEmail,
  targetDisplayName,
}: {
  adminEmail: string;
  adminDisplayName: string | null;
  targetEmail: string;
  targetDisplayName: string | null;
}) {
  const [pending, setPending] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const adminLabel = adminDisplayName ?? adminEmail;
  const targetLabel = targetDisplayName ?? targetEmail;

  async function exit() {
    if (pending) return;
    setPending(true);
    try {
      const res = await fetch('/api/platform/impersonate/exit', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        setPending(false);
        setErrMsg(`退出代操作失败 (${res.status})\n${body}\n\n请把这条信息发给 leon。`);
        return;
      }
      // Hard nav + cache-buster so the SSR layout re-reads /me with
      // the impersonate cookie now cleared. window.location.assign()
      // on the same path can no-op in some browsers; href + a fresh
      // query string forces a full reload.
      window.location.href = `/platform?_exit=${Date.now()}`;
    } catch (e) {
      setPending(false);
      setErrMsg(`退出代操作出错：${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return (
    <>
      <div className="w-full bg-warmth-dark text-paper text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 z-50 shadow-sm whitespace-nowrap overflow-x-auto">
        <span aria-hidden className="text-base leading-none">
          ↳
        </span>
        <span className="font-serif">
          管理员 <strong>{adminLabel}</strong> 正以 <strong>{targetLabel}</strong> 的身份操作
        </span>
        <button
          type="button"
          onClick={exit}
          disabled={pending}
          className="ml-2 px-3 py-0.5 rounded-full bg-paper/20 hover:bg-paper/30 text-paper text-xs font-mono tracking-wider transition disabled:opacity-60"
        >
          {pending ? '退出中…' : '退出代操作'}
        </button>
      </div>
      <AppDialog
        open={!!errMsg}
        tone="danger"
        title="退出失败"
        message={errMsg ?? ''}
        confirmLabel="知道了"
        onClose={() => setErrMsg(null)}
      />
    </>
  );
}
